<script setup>
/* eslint-disable no-console */
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAlert } from 'dashboard/composables';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';
import {
  resolveOT,
  crearOrdenTrabajo,
  consultaDetalleContratoJson,
  getPuntoServicioPorContador,
  referenceWorkOrderAquacis,
  xmlToJson,
  updateCaseToCancelled,
} from 'dashboard/api/cea';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  order: {
    type: Object,
    required: true,
  },
  aquasisData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'redirected']);

const { t } = useI18n();
const dialogRef = ref(null);
const isSubmitting = ref(false);

// Category / Subcategory selection
const categories = ref([]);
const subcategories = ref([]);
const selectedCategoryId = ref('');
const selectedSubcategoryId = ref('');
const isLoadingCategories = ref(false);
const isLoadingSubcategories = ref(false);

// Order defaults from subcategory
const orderTypes = ref([]);
const orderMotives = ref([]);
const subcategoryRepairCodes = ref([]);
const selectedOrderTypeId = ref('');
const selectedOrderMotiveId = ref('');
const selectedRepairCodeId = ref('');
const selectedOrderClass = ref('work_order');
const isLoadingDefaults = ref(false);
const hasOtConstraint = ref(false);
const hasOmConstraint = ref(false);
const allowedOrderMotives = ref([]);
const defaultOrderTypeId = ref('');
const isApplyingSubcategoryDefaults = ref(false);
const defaultsSubcategoryCode = ref('');

// Redirect reason (used as observaciones)
const reason = ref('');
const OBSERVATIONS_ALPHANUMERIC_REGEX =
  /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ.,;:\-¿?¡!()"']*$/;
const sanitizeReason = val =>
  (val || '').replace(/[^a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ.,;:\-¿?¡!()"']/g, '');
const handleReasonInput = e => {
  reason.value = sanitizeReason(e.target.value).slice(0, 100);
};

const ORDER_TYPE_MOTIVE_MAP = {
  32: ['52'],
  40: ['94'],
  33: ['70', '41'],
  6: ['61', '50'],
  23: ['66', '15'],
  21: ['86', '96', '89', '80'],
};

const selectedSubcategory = computed(() =>
  subcategories.value.find(
    s => String(s.id) === String(selectedSubcategoryId.value)
  )
);

const resolvedSubcategoryCode = computed(() => {
  const fromDefaults = defaultsSubcategoryCode.value?.trim();
  if (fromDefaults) return fromDefaults;
  return selectedSubcategory.value?.code || '';
});

// Aquasis manual override state
const aquasisLookupError = ref('');
const manualIdPtoServicio = ref('');
const showManualFields = ref(false);

const effectiveOrderMotives = computed(() => {
  if (resolvedSubcategoryCode.value === 'REP-BAP') {
    return orderMotives.value.filter(om => String(om.code) === '86');
  }

  const selectedOt = orderTypes.value.find(
    ot => String(ot.id) === String(selectedOrderTypeId.value)
  );
  const motiveCodes =
    selectedOt && ORDER_TYPE_MOTIVE_MAP[String(selectedOt.code)];
  const hasMotiveMap = Array.isArray(motiveCodes) && motiveCodes.length > 0;
  const codesSet = hasMotiveMap
    ? new Set(motiveCodes.map(String))
    : null;
  const mapFiltered = hasMotiveMap
    ? orderMotives.value.filter(om => codesSet.has(String(om.code)))
    : null;

  const constrainedBySubcategory =
    hasOmConstraint.value && allowedOrderMotives.value.length > 0;

  if (constrainedBySubcategory && hasMotiveMap) {
    const intersected = allowedOrderMotives.value.filter(om =>
      codesSet.has(String(om.code))
    );
    return intersected.length > 0 ? intersected : mapFiltered;
  }

  if (constrainedBySubcategory) {
    return allowedOrderMotives.value;
  }

  if (mapFiltered?.length) return mapFiltered;
  return orderMotives.value;
});

const effectiveOrderTypes = computed(() => {
  const list = orderTypes.value;
  if (resolvedSubcategoryCode.value === 'REP-BAP') {
    return list.filter(ot => String(ot.code) === '21');
  }
  return list;
});

const syncOrderTypeSelectionToAllowedList = () => {
  const allowed = effectiveOrderTypes.value;
  if (!allowed.length) return;
  const cur = String(selectedOrderTypeId.value);
  if (!allowed.some(ot => String(ot.id) === cur)) {
    selectedOrderTypeId.value = allowed[0].id;
  }
};

const syncOrderMotiveSelectionToAllowedList = () => {
  const allowed = effectiveOrderMotives.value;
  if (!allowed.length) return;
  const cur = String(selectedOrderMotiveId.value);
  if (!allowed.some(om => String(om.id) === cur)) {
    selectedOrderMotiveId.value = allowed[0].id;
  }
};

const isOrderTypeLocked = computed(
  () =>
    hasOtConstraint.value || resolvedSubcategoryCode.value === 'REP-BAP'
);
const isOrderMotiveLocked = computed(
  () =>
    resolvedSubcategoryCode.value === 'REP-BAP' ||
    (hasOmConstraint.value && allowedOrderMotives.value.length === 1)
);

const selectedOrderType = computed(() =>
  orderTypes.value.find(
    ot => String(ot.id) === String(selectedOrderTypeId.value)
  )
);
const isRepairCodeRequired = computed(
  () =>
    subcategoryRepairCodes.value.length > 0 &&
    selectedOrderType.value?.code === '32'
);

const isFormValid = computed(() => {
  const reasonTrimmed = reason.value?.trim() || '';
  const base =
    selectedCategoryId.value &&
    selectedSubcategoryId.value &&
    selectedOrderTypeId.value &&
    selectedOrderMotiveId.value &&
    reasonTrimmed.length > 0 &&
    reasonTrimmed.length <= 100 &&
    OBSERVATIONS_ALPHANUMERIC_REGEX.test(reasonTrimmed);
  if (!base) return false;
  if (isRepairCodeRequired.value && !selectedRepairCodeId.value) return false;
  return true;
});

const fetchCategories = async () => {
  try {
    isLoadingCategories.value = true;
    const response = await TicketCategoriesAPI.get();
    categories.value = response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    categories.value = [];
  } finally {
    isLoadingCategories.value = false;
  }
};

const fetchSubcategories = async categoryId => {
  if (!categoryId) {
    subcategories.value = [];
    return;
  }
  try {
    isLoadingSubcategories.value = true;
    const response = await TicketCategoriesAPI.getSubcategories(categoryId);
    subcategories.value = response.data;
  } catch (error) {
    console.error('Error fetching subcategories:', error);
    subcategories.value = [];
  } finally {
    isLoadingSubcategories.value = false;
  }
};

const fetchLookups = async () => {
  try {
    const [typesRes, motivesRes] = await Promise.all([
      CeaLookupsAPI.get('order_types'),
      CeaLookupsAPI.get('order_motives'),
    ]);
    orderTypes.value = typesRes.data;
    orderMotives.value = motivesRes.data;
  } catch (error) {
    console.error('Error fetching CEA lookups:', error);
    orderTypes.value = [];
    orderMotives.value = [];
  }
};

const fetchSubcategoryDefaults = async (categoryId, subcategoryId) => {
  if (!categoryId || !subcategoryId) return;

  isApplyingSubcategoryDefaults.value = true;
  try {
    isLoadingDefaults.value = true;
    const response = await TicketCategoriesAPI.getSubcategoryDefaults(
      categoryId,
      subcategoryId
    );
    const defaults = response.data;

    defaultsSubcategoryCode.value = (defaults.subcategory_code || '').trim();

    hasOtConstraint.value = false;
    hasOmConstraint.value = false;
    allowedOrderMotives.value = [];
    subcategoryRepairCodes.value = [];
    defaultOrderTypeId.value = '';
    selectedOrderTypeId.value = '';
    selectedOrderMotiveId.value = '';
    selectedRepairCodeId.value = '';

    if (defaults.default_order_type) {
      defaultOrderTypeId.value = defaults.default_order_type.id;
      selectedOrderTypeId.value = defaults.default_order_type.id;
      hasOtConstraint.value = true;
    }

    if (defaults.order_motives?.length > 0) {
      allowedOrderMotives.value = defaults.order_motives;
      hasOmConstraint.value = true;
      if (defaults.order_motives.length === 1) {
        selectedOrderMotiveId.value = defaults.order_motives[0].id;
      }
    }

    subcategoryRepairCodes.value = defaults.repair_codes || [];
    if (subcategoryRepairCodes.value.length > 0) {
      selectedRepairCodeId.value = subcategoryRepairCodes.value[0].id;
    }
  } catch (error) {
    console.error('Error fetching subcategory defaults:', error);
  } finally {
    isLoadingDefaults.value = false;
  }
  syncOrderTypeSelectionToAllowedList();
  syncOrderMotiveSelectionToAllowedList();
  await nextTick();
  isApplyingSubcategoryDefaults.value = false;
};

const detectOrderClass = subcategoryCode => {
  if (subcategoryCode?.startsWith('SRV-')) return 'service_order';
  return 'work_order';
};

const resetOrderDefaults = () => {
  defaultOrderTypeId.value = '';
  selectedOrderTypeId.value = '';
  selectedOrderMotiveId.value = '';
  selectedRepairCodeId.value = '';
  selectedOrderClass.value = 'work_order';
  hasOtConstraint.value = false;
  hasOmConstraint.value = false;
  isApplyingSubcategoryDefaults.value = false;
  defaultsSubcategoryCode.value = '';
  allowedOrderMotives.value = [];
  subcategoryRepairCodes.value = [];
};

const onCategoryChange = () => {
  selectedSubcategoryId.value = '';
  subcategories.value = [];
  resetOrderDefaults();
  if (selectedCategoryId.value) {
    fetchSubcategories(selectedCategoryId.value);
  }
};

const onSubcategoryChange = () => {
  if (selectedSubcategoryId.value) {
    const sub = subcategories.value.find(
      s => s.id === Number(selectedSubcategoryId.value)
    );
    selectedOrderClass.value = detectOrderClass(sub?.code);
    fetchSubcategoryDefaults(
      selectedCategoryId.value,
      selectedSubcategoryId.value
    );
  } else {
    resetOrderDefaults();
  }
};

const formatOrderTypeString = () => {
  const ot = orderTypes.value.find(
    item => String(item.id) === String(selectedOrderTypeId.value)
  );
  return ot ? `${ot.code} - ${ot.description}` : '';
};

const formatRepairCodeString = () => {
  const rc = subcategoryRepairCodes.value.find(
    r => r.id === selectedRepairCodeId.value
  );
  return rc ? `${rc.code} - ${rc.description}` : '';
};

const resetForm = () => {
  selectedCategoryId.value = '';
  selectedSubcategoryId.value = '';
  subcategories.value = [];
  reason.value = '';
  resetOrderDefaults();
  aquasisLookupError.value = '';
  manualIdPtoServicio.value = '';
  showManualFields.value = false;
};

const handleClose = () => {
  resetForm();
  emit('close');
};

// --- Aquasis cancel logic (from CancelOrderDialog) ---
const cancelAquasisOrder = async () => {
  const now = new Date().toISOString();
  const payload = {
    operationalSiteID: props.aquasisData?.otData?.operationalSiteID || '',
    installationID: props.aquasisData?.otData?.installationID || '',
    systemOrigin: '',
    otClass: '0',
    otOrigin: props.order.aquasis_order_id,
    endDateOt: now,
    endLastTaskOt: now,
    finalSolution: '',
    solutionDescription: '',
    nonExecutionMotive: reason.value,
    executorIdentifier: '',
    executorName: '',
    companyExecutorIdentifier: '',
    companyExecutorName: '',
    transmitterInstalled: '0',
    language: 'es',
    suspensionLevel: '0',
    longitude: '',
    latitude: '',
    coordinatesType: '',
    codificationType: '',
    captureDate: '',
    resolutionElements: [],
  };

  const xmlDoc = await resolveOT(payload);

  const returnEl =
    xmlDoc.getElementsByTagName('return')[0] ||
    xmlDoc.getElementsByTagName('resolveOTReturn')[0] ||
    xmlDoc.getElementsByTagName('resolveOTResponse')[0];

  const resultText = returnEl?.textContent?.trim()?.toLowerCase();
  return resultText === 'true' || resultText === '1';
};

// --- Aquasis create logic (from GenerateOrderModal) ---
const createAquasisOrder = async () => {
  const contractNum = props.order.ticket?.contract_number?.trim();
  if (!contractNum) return null;

  let idPtoServicio = '';
  let exploitationCode = '';
  let contractDetail;
  try {
    contractDetail = await consultaDetalleContratoJson(contractNum);
  } catch (error) {
    console.error('Error fetching contract detail:', error);
    aquasisLookupError.value = 'No se pudo consultar el contrato en Aquasis';
    throw error;
  }

  const contrato =
    contractDetail?.GenericoContratoDTO?.contrato ||
    contractDetail?.contrato ||
    {};
  const meterSerial = contrato.numeroContador || '';
  exploitationCode = contrato.explotacion || '';

  if (!meterSerial) {
    aquasisLookupError.value =
      'El contrato no tiene numero de contador asociado';
    showManualFields.value = true;
  }

  if (meterSerial) {
    try {
      const meterXml = await getPuntoServicioPorContador(
        meterSerial,
        'WSGESTIONDEUDA'
      );
      const meterReturn = meterXml.getElementsByTagName(
        'getPuntoServicioPorContadorReturn'
      )[0];
      if (meterReturn) {
        const meterJson = xmlToJson(meterReturn);
        idPtoServicio =
          meterJson?.puntosServicioContadorDTO?.PuntosServicioContadorDTO
            ?.puntoServicioDTO?.PuntoServicioDTO?.id || '';
      }
    } catch (error) {
      console.error('Error fetching idPtoServicio:', error);
    }

    if (!idPtoServicio) {
      aquasisLookupError.value =
        'No se encontro el punto de servicio para el contador';
      showManualFields.value = true;
    }
  }

  if (manualIdPtoServicio.value?.trim()) {
    idPtoServicio = manualIdPtoServicio.value.trim();
  }

  if (!idPtoServicio) {
    const err = new Error('idPtoServicio is required');
    err.isFieldError = true;
    throw err;
  }

  const selectedOt = orderTypes.value.find(
    item => String(item.id) === String(selectedOrderTypeId.value)
  );
  const selectedOm = effectiveOrderMotives.value.find(
    item => String(item.id) === String(selectedOrderMotiveId.value)
  );
  const selectedRc =
    selectedOt?.code === '32'
      ? subcategoryRepairCodes.value.find(
          r => r.id === selectedRepairCodeId.value
        )
      : undefined;

  const payload = {
    tipoOrden: selectedOt?.code || '',
    motivoOrden: selectedOm?.code || '',
    fechaCreacionOrden: new Date().toISOString(),
    numContrato: contractNum,
    idPtoServicio,
    fechaEstimdaFin: new Date().toISOString(),
    observaciones: (reason.value || '').slice(0, 120),
    codigoReparacion: selectedRc?.code || '',
    anyoExpediente: String(new Date().getFullYear()),
  };

  const xmlDoc = await crearOrdenTrabajo(payload);

  const returnEl =
    xmlDoc.getElementsByTagName('return')[0] ||
    xmlDoc.getElementsByTagName('crearOrdenTrabajoReturn')[0] ||
    xmlDoc.getElementsByTagName('crearOrdenTrabajoResponse')[0];

  if (returnEl) {
    let aquasisOrderId = null;
    if (returnEl.children && returnEl.children.length > 0) {
      const json = xmlToJson(returnEl);
      aquasisOrderId =
        json?.numOrden ||
        json?.ordenTrabajo ||
        returnEl.textContent?.trim() ||
        null;
    } else {
      aquasisOrderId = returnEl.textContent?.trim() || null;
    }
    return { aquasisOrderId, exploitationCode };
  }

  return null;
};

const linkAquasisOrder = async aquasisOrderId => {
  const caseId =
    props.order.ticket?.custom_attributes?.caso_sn ||
    props.order.ticket?.custom_attributes?.sys_id ||
    props.order.ticket?.metadata?.caso_sn ||
    props.order.ticket?.metadata?.sys_id;

  if (!caseId || !aquasisOrderId) return;

  try {
    await referenceWorkOrderAquacis(caseId, aquasisOrderId);
  } catch (error) {
    console.error('Error linking Aquasis order to case:', error);
  }
};

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    // Step 1: Cancel original in Aquasis if it has an aquasis_order_id
    if (props.order.aquasis_order_id) {
      try {
        const cancelled = await cancelAquasisOrder();
        if (!cancelled) {
          useAlert(t('ORDERS.REDIRECT.AQUASIS_CANCEL_ERROR'));
          return;
        }
      } catch (error) {
        console.error('Error cancelling Aquasis order:', error);
        useAlert(t('ORDERS.REDIRECT.AQUASIS_CANCEL_ERROR'));
        return;
      }

      // Cancel CEA case (best-effort)
      const caseId =
        props.order.ticket?.custom_attributes?.caso_sn ||
        props.order.ticket?.custom_attributes?.sys_id ||
        props.order.ticket?.metadata?.caso_sn ||
        props.order.ticket?.metadata?.sys_id;
      if (caseId) {
        updateCaseToCancelled(caseId).catch(err =>
          console.error('Failed to cancel CEA case:', err)
        );
      }
    }

    // Step 2: Create new Aquasis order if contract is available
    let aquasisOrderId = null;
    let exploitationCode = '';
    let aquasisWarning = false;
    const contractNum = props.order.ticket?.contract_number?.trim();

    if (contractNum) {
      try {
        const result = await createAquasisOrder();
        if (result) {
          aquasisOrderId = result.aquasisOrderId;
          exploitationCode = result.exploitationCode || '';
        }
      } catch (error) {
        console.error('Error creating Aquasis order:', error);
        if (error.isFieldError) {
          isSubmitting.value = false;
          return;
        }
        aquasisWarning = true;
      }
    }

    // Step 3: Call backend redirect endpoint
    const newOrderData = {
      new_subcategory_id: selectedSubcategoryId.value,
      reason: reason.value,
      order_class: selectedOrderClass.value,
      order_type: formatOrderTypeString(),
      order_type_record_id: selectedOrderTypeId.value,
      order_motive_id: selectedOrderMotiveId.value,
      observations: reason.value,
    };

    if (
      selectedRepairCodeId.value &&
      selectedOrderType.value?.code === '32'
    ) {
      newOrderData.repair_code = formatRepairCodeString();
      newOrderData.repair_code_record_id = selectedRepairCodeId.value;
    }

    if (aquasisOrderId) {
      newOrderData.aquasis_order_id = aquasisOrderId;
    }
    if (exploitationCode) {
      newOrderData.aquasis_exploitation_code = exploitationCode;
    }

    const response = await ServiceOrdersAPI.redirect(
      props.order.id,
      newOrderData
    );

    // Step 4: Link new Aquasis order to CEA case
    if (aquasisOrderId) {
      await linkAquasisOrder(aquasisOrderId);
    }

    if (aquasisWarning) {
      useAlert(t('ORDERS.REDIRECT.AQUASIS_CREATE_WARNING'));
    } else {
      useAlert(t('ORDERS.REDIRECT.SUCCESS'));
    }

    emit('redirected', response.data);
    handleClose();
  } catch (error) {
    console.error('Error redirecting order:', error);
    useAlert(t('ORDERS.REDIRECT.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const initModal = async () => {
  await Promise.all([fetchCategories(), fetchLookups()]);
};

watch(selectedOrderTypeId, () => {
  if (isApplyingSubcategoryDefaults.value) return;
  selectedOrderMotiveId.value = '';
  selectedRepairCodeId.value = '';
  const motives = effectiveOrderMotives.value;
  if (motives.length === 1) {
    selectedOrderMotiveId.value = motives[0].id;
  }
});

watch(
  effectiveOrderMotives,
  motives => {
    if (!selectedOrderMotiveId.value) {
      if (motives.length === 1) {
        selectedOrderMotiveId.value = motives[0].id;
      }
      return;
    }
    const stillValid = motives.some(
      m => String(m.id) === String(selectedOrderMotiveId.value)
    );
    if (!stillValid) {
      selectedOrderMotiveId.value = '';
      if (motives.length === 1) {
        selectedOrderMotiveId.value = motives[0].id;
      }
    }
  },
  { flush: 'post' }
);

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      resetForm();
      dialogRef.value?.open();
      initModal();
    } else {
      dialogRef.value?.close();
    }
  }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('ORDERS.REDIRECT.TITLE')"
    overflow-y-auto
    :is-loading="isSubmitting"
    :confirm-button-label="
      isSubmitting
        ? t('ORDERS.REDIRECT.SUBMITTING')
        : t('ORDERS.REDIRECT.BUTTON')
    "
    :disable-confirm-button="!isFormValid || isSubmitting"
    @close="handleClose"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <!-- Order reference card -->
      <div class="bg-n-solid-3 rounded-lg p-3 border border-n-weak">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-11">
            {{ $t('ORDERS.ID') }} #{{ order.id }}
          </span>
          <span
            v-if="order.aquasis_order_id"
            class="font-mono text-sm font-semibold text-n-brand"
          >
            {{ $t('ORDERS.AQUASIS_COL') }}: {{ order.aquasis_order_id }}
          </span>
        </div>
      </div>

      <!-- Aquasis warning banner -->
      <div
        v-if="order.aquasis_order_id"
        class="p-3 rounded-lg bg-n-amber-3 border border-n-amber-6"
      >
        <div class="flex items-start gap-2">
          <span
            class="i-lucide-alert-triangle text-n-amber-11 mt-0.5 shrink-0"
          />
          <p class="text-xs text-n-amber-11">
            {{ $t('ORDERS.REDIRECT.AQUASIS_WARNING') }}
          </p>
        </div>
      </div>

      <!-- Category dropdown -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('ORDERS.REDIRECT.CATEGORY_LABEL') }}
          <span class="text-n-ruby-11">*</span>
        </label>
        <select
          v-model="selectedCategoryId"
          :disabled="isLoadingCategories"
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand disabled:opacity-50"
          @change="onCategoryChange"
        >
          <option value="" disabled>
            {{
              isLoadingCategories
                ? 'Cargando...'
                : $t('ORDERS.REDIRECT.SELECT_CATEGORY')
            }}
          </option>
          <option v-for="cat in categories" :key="cat.id" :value="cat.id">
            {{ cat.code }} - {{ cat.name }}
          </option>
        </select>
      </div>

      <!-- Subcategory dropdown -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('ORDERS.REDIRECT.SUBCATEGORY_LABEL') }}
          <span class="text-n-ruby-11">*</span>
        </label>
        <select
          v-model="selectedSubcategoryId"
          :disabled="!selectedCategoryId || isLoadingSubcategories"
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand disabled:opacity-50"
          @change="onSubcategoryChange"
        >
          <option value="" disabled>
            {{
              isLoadingSubcategories
                ? 'Cargando...'
                : $t('ORDERS.REDIRECT.SELECT_SUBCATEGORY')
            }}
          </option>
          <option v-for="sub in subcategories" :key="sub.id" :value="sub.id">
            {{ sub.code }} - {{ sub.name }}
          </option>
        </select>
      </div>

      <!-- Auto-populated order fields (shown after subcategory selection) -->
      <template v-if="selectedSubcategoryId">
        <!-- Order Type -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('ORDERS.DETAIL.ORDER_TYPE') }}
            <span class="text-n-ruby-11">*</span>
          </label>
          <select
            v-model="selectedOrderTypeId"
            :disabled="isLoadingDefaults || isOrderTypeLocked"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand disabled:opacity-50"
          >
            <option value="" disabled>
              {{ isLoadingDefaults ? 'Cargando...' : 'Seleccione un tipo' }}
            </option>
            <option v-for="ot in effectiveOrderTypes" :key="ot.id" :value="ot.id">
              {{ ot.code }} - {{ ot.description }}
            </option>
          </select>
          <p v-if="isOrderTypeLocked" class="text-xs text-n-slate-10">
            Asignado automaticamente por la subcategoria
          </p>
        </div>

        <!-- Order Motive -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('ORDERS.DETAIL.ORDER_MOTIVE') }}
            <span class="text-n-ruby-11">*</span>
          </label>
          <select
            v-model="selectedOrderMotiveId"
            :disabled="isLoadingDefaults || isOrderMotiveLocked"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand disabled:opacity-50"
          >
            <option value="" disabled>
              {{ isLoadingDefaults ? 'Cargando...' : 'Seleccione un motivo' }}
            </option>
            <option
              v-for="om in effectiveOrderMotives"
              :key="om.id"
              :value="om.id"
            >
              {{ om.code }} - {{ om.description }}
            </option>
          </select>
          <p v-if="isOrderMotiveLocked" class="text-xs text-n-slate-10">
            Asignado automaticamente por la subcategoria
          </p>
        </div>

        <!-- Repair Code (solo tipo 32) -->
        <div
          v-if="
            selectedOrderType?.code === '32' &&
            subcategoryRepairCodes.length > 0
          "
          class="flex flex-col gap-1"
        >
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('ORDERS.DETAIL.REPAIR_CODE') }}
            <span v-if="isRepairCodeRequired" class="text-n-ruby-11">*</span>
          </label>
          <select
            v-model="selectedRepairCodeId"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option value="">
              {{
                isRepairCodeRequired
                  ? 'Seleccione un codigo (obligatorio)'
                  : 'Seleccione un codigo (opcional)'
              }}
            </option>
            <option
              v-for="rc in subcategoryRepairCodes"
              :key="rc.id"
              :value="rc.id"
            >
              {{ rc.code }} - {{ rc.description }}
            </option>
          </select>
        </div>
      </template>

      <!-- Manual idPtoServicio (shown when auto-lookup failed) -->
      <div
        v-if="showManualFields"
        class="p-3 rounded-lg bg-n-amber-3 border border-n-amber-6"
      >
        <p class="text-xs text-n-amber-11 mb-2">
          {{ aquasisLookupError }}
        </p>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            Punto de Servicio (idPtoServicio)
          </label>
          <input
            v-model="manualIdPtoServicio"
            type="text"
            placeholder="Ingrese el ID del punto de servicio"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>
      </div>

      <!-- Redirect Reason -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('ORDERS.REDIRECT.REASON_LABEL') }}
          <span class="text-n-ruby-11">*</span>
        </label>
        <textarea
          :value="reason"
          rows="3"
          maxlength="100"
          :placeholder="$t('ORDERS.REDIRECT.REASON_PLACEHOLDER')"
          class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
          @input="handleReasonInput"
        />
        <span class="text-xs text-n-slate-10 text-right block">
          {{ reason.length }}/100
        </span>
      </div>

      <div class="text-xs text-n-slate-10">
        <span class="text-n-ruby-11">*</span> Campos requeridos
      </div>
    </div>
  </Dialog>
</template>
